import React from 'react'
import {
  Edit,
  SimpleForm,
  BooleanInput,
  DateTimeInput,
  TextInput,
  SelectInput,
  Toolbar,
  SaveButton,
  DeleteWithConfirmButton,
  FormDataConsumer,
  useRecordContext,
} from 'react-admin';
import { Box, Divider, Typography } from '@mui/material';
import { getEcgStatus } from './ecgStatus';

// Persisted ECG state (computed from the loaded record, not the form values)
const EcgCurrentStatus = () => {
  const record = useRecordContext();
  if (!record) return null;
  const status = getEcgStatus(record);
  return (
    <Typography variant="body2" sx={{ color: status.color }}>
      Current status: {status.label}
      {status.dates.length > 0 && ` — ${status.dates.join(', ')}`}
    </Typography>
  );
};

// When full access is on, the from/until dates are shown read-only:
// `ecg_enabled_until` only bounds the window when the permanent flag is off.
const EcgEnabledDatesInfo = ({ formData }) => {
  const fmt = (v) => new Date(v).toLocaleDateString();
  const lines = [
    formData.ecg_enabled_from ? `Since ${fmt(formData.ecg_enabled_from)}` : 'Starts when saved',
    formData.ecg_enabled_until ? `Until ${fmt(formData.ecg_enabled_until)}` : null,
  ].filter(Boolean);
  return (
    <Typography variant="body2" color="text.secondary">
      {lines.join(' — ')}
    </Typography>
  );
};

const UserEditToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton />
    <DeleteWithConfirmButton />
  </Toolbar>
);

const UserEdit = (props) => {
  return (
    <Edit {...props} redirect="show" mutationMode="pessimistic">
      <SimpleForm sanitizeEmptyValues toolbar={<UserEditToolbar />}>
        <Box display="flex" flexDirection="column" gap={2} width={{ xs: '100%', md: '50ch' }}>
          <TextInput source="firstname" fullWidth />
          <TextInput source="lastname" fullWidth />
          <TextInput source="email" fullWidth />
          <TextInput source="phone_number" label="Phone" fullWidth />
          <SelectInput
            source="lang"
            label="Language"
            choices={[
              { id: 'fr', name: 'French' },
              { id: 'en', name: 'English' },
            ]}
            fullWidth
          />
          <TextInput
            source="roles"
            label="Roles (comma separated, e.g. ROLE_ADMIN)"
            parse={v => (v ? v.split(',').map(r => r.trim()).filter(Boolean) : [])}
            format={v => (Array.isArray(v) ? v.join(', ') : (v || ''))}
            fullWidth
          />
          <BooleanInput source="activated" label="Activated" />
          <BooleanInput source="banned" label="Banned" />
          <Divider textAlign="left">ECG access</Divider>
          <EcgCurrentStatus />
          <BooleanInput source='ecg_enabled_full' label={"ECG enabled without expiration"} />
          <FormDataConsumer>
            {({ formData }) =>
              formData?.ecg_enabled_full ? (
                <EcgEnabledDatesInfo formData={formData} />
              ) : (
                <>
                  <DateTimeInput source='ecg_trial_end_date' label={"ECG trial end date"} />
                  <DateTimeInput source='ecg_enabled_from' label={"ECG enabled from"} />
                  <DateTimeInput source='ecg_enabled_until' label={"ECG enabled until"} />
                </>
              )
            }
          </FormDataConsumer>
        </Box>
      </SimpleForm>
    </Edit>
  )
};

export default UserEdit
